import { TemplateTypes } from "Enums";

import {
    Button,
    Link,
    DateTime,
    Date,
    Time,
    Checkbox,
    HtmlEditor,
    Number,
    Image,
    FileUpload,
    ComboBox,
    List,
    TextBlock,
    Text,
    HiddenText,
    HiddenDate,
    ContextMenu,
    UnsupportedControl,
    MaskedTextBox,
    RtfEditor,
    DatabaseImage,
    ThreeStateCheckbox,
    ColorPicker,
    Custom,
    MultiSelect,
    AutoComplete,
    PeoplePicker,
    DateTimeNoTimeZone,
    UserPicker,
} from "Controls";

import { Grid, EasyEdit, Filter, Form, Tree, Dashboard, TreeGrid, Board } from "Grids";

import { ControlTypeConfiguration, ControlFactory } from "Configuration/ControlTypeConfiguration";
import { GridTypeConfiguration, GridFactory } from "Configuration/GridTypeConfiguration";
import { GridSettings } from "Configuration/GridSettings";
import { GridDefinition, FormDefinition, DashboardDefinition, GridControlTypes, GridTypes, BoardDefinition } from "Service/Client";

/**
 * ## Configuration
 *
 * The configuration enables you to overwrite settings of controls and grids.
 *
 * You place the changes in the configuration in the CustomConfiguration.js in your Content/Scripts folder.
 * This custom configuration won't be overwritten on future updates.
 * @since 4.0.0
 */
export class Configuration {
    private static controlTypes: { [controlTypeId: number]: ControlTypeConfiguration } = {};

    private static gridTypes: { [gridTypeId: number]: GridTypeConfiguration } = {};

    /**
     * The base url for the default dms api endpoint.
     * @since 4.0.0
     */
    public static baseUrl: string = "";

    /*
      @hidden
    */
    public static isDmsConfig: boolean = false;

    /**
     * Create new GridSettings with default values.
     * @since 4.0.0
     */
    public static createDefaultGridSettings(): GridSettings {
        let gridSettings = new GridSettings();
        let defaultUrlSet = this.baseUrl + "command/set/";
        let defaultUrlGet = this.baseUrl + "command/get/";

        gridSettings.dataUrlSet = defaultUrlSet;
        gridSettings.dataUrlGet = defaultUrlGet;
        return gridSettings;
    }

    /**
     * Initialize all GridTypes, ControlTypes, Templates, etc...
     * @hidden
     */
    public static initialize() {
        this.controlTypes[GridControlTypes.Text] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Text(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.Number] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Number(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.Date] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Date(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.Checkbox] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Checkbox(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.List] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new List(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.Image] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Image(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.TextBlock] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new TextBlock(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.HiddenText] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new HiddenText(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.HiddenDate] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new HiddenDate(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.DateTime] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new DateTime(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.Time] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Time(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.TextTextBlock] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Text(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.MaskedTextBox] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new MaskedTextBox(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.ComboBox] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new ComboBox(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.DropDown)
        );
        this.controlTypes[GridControlTypes.HtmlEditor] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new HtmlEditor(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.FileUpload] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new FileUpload(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.ContextMenu] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new ContextMenu(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.RtfEditor] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new RtfEditor(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.SelectDialog] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new UnsupportedControl(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.DatabaseImage] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new DatabaseImage(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.Edit] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Button(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.Cancel] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Button(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.Add] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Button(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.Update] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Button(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.Delete] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Button(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.Command] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Button(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.Link] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Link(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.SelectButton] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Button(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.MultiPageUpdate] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Button(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.ThreeStateCheckbox] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new ThreeStateCheckbox(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.ColorPicker] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new ColorPicker(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.Custom] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Custom(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );
        this.controlTypes[GridControlTypes.MultiSelect] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new MultiSelect(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.DropDown)
        );
        this.controlTypes[GridControlTypes.AutoComplete] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new AutoComplete(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.DropDown)
        );
        this.controlTypes[GridControlTypes.PeoplePicker] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new PeoplePicker(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.PeoplePicker)
        );
        this.controlTypes[GridControlTypes.UserPicker] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new UserPicker(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.User)
        );
        this.controlTypes[GridControlTypes.DatasetCommand] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Button(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.ColumnSelectorCommand] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new Button(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment, serviceProvider.Command)
        );
        this.controlTypes[GridControlTypes.DateTimeNoTimeZone] = new ControlTypeConfiguration(
            (controlDefinition, textBlockCategory, serviceProvider, environment) =>
                new DateTimeNoTimeZone(controlDefinition, textBlockCategory, serviceProvider.TextBlock, environment)
        );

        let gridSettings = this.createDefaultGridSettings();
        gridSettings.setTemplate(TemplateTypes.Wrapper, Grid.wrapperTemplate);
        this.gridTypes[GridTypes.Grid] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider, environment) =>
                new Grid(
                    <GridDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    environment,
                    serviceProvider.DropDown,
                    serviceProvider.Command,
                    serviceProvider.Export,
                    serviceProvider.GridDefinition,
                    serviceProvider.TextBlock,
                    serviceProvider.DatabaseImage
                ),
            gridSettings
        );

        let easyEditGridSettings = this.createDefaultGridSettings();
        easyEditGridSettings.setTemplate(TemplateTypes.Wrapper, EasyEdit.wrapperTemplate);

        this.gridTypes[GridTypes.EasyEdit] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider, environment) =>
                new EasyEdit(
                    <GridDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    environment,
                    serviceProvider.DropDown,
                    serviceProvider.Command,
                    serviceProvider.Export,
                    serviceProvider.GridDefinition,
                    serviceProvider.TextBlock,
                    serviceProvider.DatabaseImage
                ),
            easyEditGridSettings
        );

        let formGridSettings = this.createDefaultGridSettings();
        formGridSettings.setTemplate(TemplateTypes.Wrapper, Form.wrapperTemplate);
        formGridSettings.setTemplate(TemplateTypes.Content, Form.formContentTemplate);
        formGridSettings.setTemplate(TemplateTypes.Tab, Form.tabTemplate);
        formGridSettings.setTemplate(TemplateTypes.Group, Form.groupTemplate);
        formGridSettings.setTemplate(TemplateTypes.Columns, Form.columnsTemplate);
        formGridSettings.setTemplate(TemplateTypes.Column, Form.columnTemplate);

        this.gridTypes[GridTypes.Form] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider, environment) =>
                new Form(
                    <FormDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    environment,
                    serviceProvider.DropDown,
                    serviceProvider.Command,
                    serviceProvider.GridDefinition,
                    serviceProvider.TextBlock,
                    serviceProvider.DatabaseImage
                ),
            formGridSettings
        );

        let filterGridSettings = this.createDefaultGridSettings();
        filterGridSettings.setTemplate(TemplateTypes.Wrapper, Filter.wrapperTemplate);
        filterGridSettings.setTemplate(TemplateTypes.Content, Form.formContentTemplate);
        filterGridSettings.setTemplate(TemplateTypes.Tab, Form.tabTemplate);
        filterGridSettings.setTemplate(TemplateTypes.Group, Form.groupTemplate);
        filterGridSettings.setTemplate(TemplateTypes.Columns, Form.columnsTemplate);
        filterGridSettings.setTemplate(TemplateTypes.Column, Form.columnTemplate);

        this.gridTypes[GridTypes.Filter] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider, environment) =>
                new Filter(
                    <FormDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    environment,
                    serviceProvider.DropDown,
                    serviceProvider.Command,
                    serviceProvider.GridDefinition,
                    serviceProvider.TextBlock,
                    serviceProvider.DatabaseImage
                ),
            filterGridSettings
        );

        let treeSettings = this.createDefaultGridSettings();
        treeSettings.dataUrlGet = this.baseUrl + "command/getHierarchical/";
        treeSettings.setTemplate(TemplateTypes.Wrapper, Tree.wrapperTemplate);
        this.gridTypes[GridTypes.Tree] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider, environment) =>
                new Tree(
                    <GridDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    environment,
                    serviceProvider.DropDown,
                    serviceProvider.Command,
                    serviceProvider.GridDefinition,
                    serviceProvider.TextBlock,
                    serviceProvider.DatabaseImage
                ),
            treeSettings
        );

        let dashboardSettings = this.createDefaultGridSettings();
        dashboardSettings.setTemplate(TemplateTypes.Wrapper, Dashboard.wrapperTemplate);
        dashboardSettings.setTemplate(TemplateTypes.Content, Dashboard.contentTemplate);
        dashboardSettings.setTemplate(TemplateTypes.Column, Dashboard.columnTemplate);

        this.gridTypes[GridTypes.Dashboard] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider, environment) =>
                new Dashboard(
                    <DashboardDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    environment,
                    serviceProvider.DropDown,
                    serviceProvider.Command,
                    serviceProvider.TextBlock,
                    serviceProvider.GridDefinition,
                    serviceProvider.DatabaseImage
                ),
            dashboardSettings
        );

        let treeGridSettings = this.createDefaultGridSettings();
        treeGridSettings.setTemplate(TemplateTypes.Wrapper, TreeGrid.wrapperTemplate);
        this.gridTypes[GridTypes.TreeGrid] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider, environment) =>
                new TreeGrid(
                    <GridDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    environment,
                    serviceProvider.DropDown,
                    serviceProvider.Command,
                    serviceProvider.Export,
                    serviceProvider.GridDefinition,
                    serviceProvider.TextBlock,
                    serviceProvider.DatabaseImage
                ),
            treeGridSettings
        );

        const boardSettings = this.createDefaultGridSettings();
        boardSettings.setTemplate(TemplateTypes.Wrapper, Board.wrapperTemplate);
        boardSettings.setTemplate(TemplateTypes.Content, Board.contentTemplate);
        boardSettings.setTemplate(TemplateTypes.Column, Board.columnTemplate);
        boardSettings.setTemplate(TemplateTypes.Columns, Board.columnsTemplate);
        this.gridTypes[GridTypes.Board] = new GridTypeConfiguration(
            (gridDefinition, textBlockCategory, gridSettings, serviceProvider, environment) =>
                new Board(
                    <BoardDefinition>gridDefinition,
                    textBlockCategory,
                    gridSettings,
                    environment,
                    serviceProvider.DropDown,
                    serviceProvider.Command,
                    serviceProvider.GridDefinition,
                    serviceProvider.TextBlock,
                    serviceProvider.DatabaseImage
                ),
            boardSettings
        );
    }

    /**
     * Get GridTypeConfiguration from internal array.
     * @param gridType
     */
    private static _getGridTypeConfiguration(gridType: GridTypes): GridTypeConfiguration {
        return this.gridTypes[gridType] as GridTypeConfiguration;
    }

    /**
     * Gets the specific type configuration for given GridType.
     * @param gridType GridType.
     * @since 4.0.0
     */
    public static getGridTypeConfiguration(gridType: GridTypes): GridTypeConfiguration {
        return this._getGridTypeConfiguration(gridType);
    }

    /**
     * Gets the factory for GridType.
     * @param gridType GridType
     */
    public static getGridFactory(gridType: GridTypes): GridFactory {
        let gridTypeConfiguration = this._getGridTypeConfiguration(gridType);
        if (gridTypeConfiguration != null) {
            return gridTypeConfiguration.factory;
        }
        return null;
    }

    /**
     * Get ControlTypeConfiguration from internal array.
     * @param controlType
     */
    private static _getControlTypeConfiguration(controlType: GridControlTypes): ControlTypeConfiguration {
        return this.controlTypes[controlType] as ControlTypeConfiguration;
    }

    /**
     * Gets the specific type configuration for GridControlType.
     * @param controlType
     */
    public static getControlTypeConfiguration(controlType: GridControlTypes): ControlTypeConfiguration {
        return this._getControlTypeConfiguration(controlType);
    }

    /**
     * Gets the factory for GridControlType.
     * @param controlType
     */
    public static getControlFactory(controlType: GridControlTypes): ControlFactory {
        let controlTypeConfiguration = this._getControlTypeConfiguration(controlType);
        if (controlTypeConfiguration != null) {
            return controlTypeConfiguration.controlFactory;
        }
        return null;
    }
}

Configuration.initialize();
